/**
 * THE COGENT'S FACES: expressions made by swapping the chargen face parts
 * (its Eyes, Brow and Mouth) in place on the character, over whatever look
 * it wears. Adapted from the Polyworld agent's expression set to this
 * wardrobe's item ids. No three.js here.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import type { Clip } from "./character";
import type { Look } from "./wardrobe";

/** The faces, as wardrobe items per face category. */
export const EXPRESSIONS = {
  grin: { Mouth: "mouths/11_toothy_grin", Brow: "eyebrows/10_mischievous" },
  surprised: {
    Mouth: "mouths/07_surprised",
    Brow: "eyebrows/07_surprised",
    Eyes: "eyes/05_open",
  },
  tongue: { Mouth: "mouths/09_tongue_out", Brow: "eyebrows/14_cocky" },
  sly: {
    Mouth: "mouths/16_sly_grin",
    Brow: "eyebrows/05_skeptical",
    Eyes: "eyes/08_sly",
  },
  sleepy: { Mouth: "mouths/03_neutral", Eyes: "eyes/12_sleepy" },
  curious: {
    Mouth: "mouths/13_side_smile",
    Brow: "eyebrows/15_concerned",
    Eyes: "eyes/03_curious",
  },
  laugh: { Mouth: "mouths/15_big_laugh", Brow: "eyebrows/01_soft_arch" },
  happy: { Mouth: "mouths/08_happy_open", Brow: "eyebrows/01_soft_arch" },
  starstruck: {
    Mouth: "mouths/08_happy_open",
    Brow: "eyebrows/07_surprised",
    Eyes: "eyes/13_star",
  },
} as const satisfies Record<
  string,
  Partial<Record<"Eyes" | "Brow" | "Mouth", string>>
>;

export type Expression = keyof typeof EXPRESSIONS;

/** The categories a face swaps: changing only these never changes how far the character reaches. */
export const FACE_PARTS: readonly string[] = ["Eyes", "Brow", "Mouth"];

/** `look` wearing `expression`'s face parts. */
export function withExpression(
  look: Look,
  expression: Expression | null,
): Look {
  if (!expression) return look;
  return { ...look, parts: { ...look.parts, ...EXPRESSIONS[expression] } };
}

/** The face each motion wears: falling is a surprise, landing a grin, and each hover clip its own little dance face. */
const CLIP_FACES: Record<Clip, Expression> = {
  fall: "surprised",
  land: "grin",
  dance: "starstruck",
  jog: "laugh",
  spell: "sly",
  sword: "grin",
  interact: "happy",
};

/** A short passive moment at rest, every so often: a face, held for `ms`. */
const PASSIVE_BEATS: readonly { face: Expression; ms: number }[] = [
  { face: "grin", ms: 1400 },
  { face: "surprised", ms: 1100 },
  { face: "tongue", ms: 1200 },
  { face: "sly", ms: 1600 },
  { face: "sleepy", ms: 1800 },
  { face: "curious", ms: 2600 },
];
/** Quiet time between passive beats, in ms. */
const PASSIVE_GAP: [number, number] = [6000, 15000];

/** A pleased hello: the face a click or a fresh line wears. */
export const POKE: { face: Expression; ms: number } = {
  face: "happy",
  ms: 1500,
};

/**
 * THE FACE DIRECTOR: what face the character makes now. A beat (an emote,
 * a line it says) wins for its time; otherwise the clip it plays sets the
 * face; at rest it makes a passive face every so often (`passive`). Under
 * reduced motion it keeps its own face: no expressions at all.
 */
export function useFace(clip: Clip | null, passive: boolean) {
  const [beat, setBeat] = useState<Expression | null>(null);
  const [reduced, setReduced] = useState(false);
  const timer = useRef(0);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const set = () => setReduced(mq.matches);
    set();
    mq.addEventListener("change", set);
    return () => mq.removeEventListener("change", set);
  }, []);
  const play = useCallback((face: Expression, ms: number) => {
    window.clearTimeout(timer.current);
    setBeat(face);
    timer.current = window.setTimeout(() => setBeat(null), ms);
  }, []);
  // At rest, now and then: a passive beat.
  useEffect(() => {
    if (!passive || reduced || clip || beat) return;
    const [min, max] = PASSIVE_GAP;
    const t = window.setTimeout(
      () => {
        const b =
          PASSIVE_BEATS[Math.floor(Math.random() * PASSIVE_BEATS.length)]!;
        play(b.face, b.ms);
      },
      min + Math.random() * (max - min),
    );
    return () => window.clearTimeout(t);
  }, [passive, reduced, clip, beat, play]);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const face = reduced ? null : (beat ?? (clip ? CLIP_FACES[clip] : null));
  return { face, play };
}

/** The face to say a line with, read from how it ends: a question is curious, an exclamation happy, else a grin. */
export function faceForLine(text: string): Expression {
  const t = text.trim();
  if (t.endsWith("?")) return "curious";
  if (t.endsWith("!")) return "happy";
  return "grin";
}
